import React, { useState, useEffect, useMemo, useCallback, createContext } from "react";
import { useHistory } from "react-router-dom";
import isNativeApp from "../../utils/isNativeApp";

// Matches a chat route (/tickets/<uuid>) — used to detect chat→chat switches.
const TICKET_CHAT_ROUTE_RE = /^\/tickets\/[0-9a-f-]{36}/i;

// WhatsApp-parity back-stack applies on mobile only: the native app (any
// width) or a narrow browser viewport (<960px = MUI "md" breakpoint, the same
// threshold the ticket screens use for their mobile layout).
export const isMobileClient = () =>
    isNativeApp() || (typeof window !== "undefined" && window.innerWidth < 960);

const TicketsContext = createContext();

const TicketsContextProvider = ({ children }) => {
	const [currentTicket, setCurrentTicket] = useState({ id: null, code: null });
    const history = useHistory();

    useEffect(() => {
        if (currentTicket?.uuid) {
            const targetPath = `/tickets/${currentTicket.uuid}`;
            // Skip the push when we are already on the target ticket URL —
            // otherwise every chat open produces a duplicate history entry
            // (navigateToTicket already pushed, or the URL is already there).
            if (history.location.pathname !== targetPath) {
                history.push(targetPath);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTicket])

    // Navigate to a ticket immediately (same event loop tick) instead of
    // waiting for the useEffect above to fire after the next render cycle.
    // The effect is kept as a fallback for any callers that use setCurrentTicket
    // directly, but new callers should use navigateToTicket to avoid the
    // 1-frame delay that caused the "needs 2 clicks" symptom.
    const navigateToTicket = useCallback((ticket) => {
        setCurrentTicket(ticket);
        if (ticket?.uuid) {
            const targetPath = `/tickets/${ticket.uuid}`;
            // Skip the push when already on the target ticket URL (e.g.
            // Accept-from-inside-ticket) to avoid a redundant history entry.
            if (history.location.pathname !== targetPath) {
                // WhatsApp back-stack parity (mobile only): switching from one
                // chat straight to another REPLACES the history entry instead
                // of pushing, so a single hardware/browser back from any chat
                // always lands on the ticket list — never on a previous chat.
                // List→chat keeps push (one back returns to the list), and
                // desktop behavior is unchanged.
                if (
                    isMobileClient() &&
                    TICKET_CHAT_ROUTE_RE.test(history.location.pathname)
                ) {
                    history.replace(targetPath);
                } else {
                    history.push(targetPath);
                }
            }
        }
    }, [history]);

	const ticketsValue = useMemo(
		() => ({ currentTicket, setCurrentTicket, navigateToTicket }),
		[currentTicket, navigateToTicket]
	);

	return (
		<TicketsContext.Provider value={ticketsValue}>
			{children}
		</TicketsContext.Provider>
	);
};

export { TicketsContext, TicketsContextProvider };
